/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-anonymous-default-export */
import React, { useCallback, useRef, useState } from 'react'
import { Input, Button, Icon, FormPro } from '@roo/roo'
import Modal from '@roo/roo/Modal'
import PopConfirm from '@roo/roo/PopConfirm'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import {
  fetchGroupList,
  createGroup,
  updateGroup,
  deleteGroup,
  IGroupItem,
  IGroupConflictItem,
} from '@/pages/staff/common/groupApi'
import { failToast, successToast } from '@/common/utils/toast'
import CommonTable from '@/components/CommonTable'
import TipText from '@/components/TipText'
import styles from './index.module.less'

type ModalType = 'create' | 'edit'

interface GroupModalState {
  visible: boolean;
  type: ModalType;
  record?: IGroupItem;
}

export default () => {
  const { t } = useTranslation()
  const [keyword, setKeyword] = useState('')
  const [inputVal, setInputVal] = useState('')
  const [pageNo, setPageNo] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const pageNoRef = useRef(1)
  const pageSizeRef = useRef(10)
  const keywordRef = useRef('')
  const [groupModal, setGroupModal] = useState<GroupModalState>({ visible: false, type: 'create' })
  const [deletingGroup, setDeletingGroup] = useState<IGroupItem | null>(null)
  const [form] = FormPro.useForm()

  // 新增/编辑弹窗行数据（脱离 FormPro.List，用原生 state 管理）
  type CreateRow = { key: string; name: string; description: string; nameError?: string }
  const [createRows, setCreateRows] = useState<CreateRow[]>([{ key: `row-${Date.now()}`, name: '', description: '' }])

  const handleRowNameChange = useCallback((index: number, value: string) => {
    setCreateRows(prev => prev.map((row, i) => i === index ? { ...row, name: value, nameError: undefined } : row))
  }, [])

  const handleRowNameBlur = useCallback(() => {
    setCreateRows(prev => {
      // 统计各名称出现次数
      const nameCount: Record<string, number> = {}
      prev.forEach(row => {
        const name = row.name.trim()
        if (name) nameCount[name] = (nameCount[name] || 0) + 1
      })
      return prev.map(row => {
        const name = row.name.trim()
        if (name && nameCount[name] > 1) {
          // 重名：标记错误
          return { ...row, nameError: t('e_pc_group_dept_exist', '存在重复分组，请调整') }
        }
        // 名称唯一或为空：清除重名错误（空值错误保留）
        if (row.nameError === t('e_pc_group_dept_exist', '存在重复分组，请调整')) {
          return { ...row, nameError: undefined }
        }
        return row
      })
    })
  }, [t])

  const handleRowDescChange = useCallback((index: number, value: string) => {
    setCreateRows(prev => prev.map((row, i) => i === index ? { ...row, description: value } : row))
  }, [])

  const handleRowAdd = useCallback(() => {
    setCreateRows(prev => [...prev, { key: `row-${Date.now()}`, name: '', description: '' }])
  }, [])

  const handleRowRemove = useCallback((index: number) => {
    setCreateRows(prev => {
      const filtered = prev.filter((_, i) => i !== index)
      // 删除后重新扫描重名状态
      const nameCount: Record<string, number> = {}
      filtered.forEach(row => {
        const name = row.name.trim()
        if (name) nameCount[name] = (nameCount[name] || 0) + 1
      })
      return filtered.map(row => {
        const name = row.name.trim()
        if (name && nameCount[name] > 1) return { ...row, nameError: t('e_pc_group_dept_exist', '存在重复分组，请调整') }
        if (row.nameError === t('e_pc_group_dept_exist', '存在重复分组，请调整')) return { ...row, nameError: undefined }
        return row
      })
    })
  }, [t])

  const { data: listData, loading, run: fetchList } = useRequest(
    (p: number, ps: number, k: string) => fetchGroupList({ pageNo: p, pageSize: ps, keyword: k }).then(res => res.data),
    { manual: true }
  )

  const doFetch = useCallback((p: number, ps: number, k: string) => {
    pageNoRef.current = p
    pageSizeRef.current = ps
    keywordRef.current = k
    fetchList(p, ps, k)
  }, [fetchList])

  // 初始加载
  React.useEffect(() => {
    doFetch(1, pageSizeRef.current, '')
  }, [])

  const handleSearch = () => {
    doFetch(1, pageSizeRef.current, inputVal)
    setPageNo(1)
    setKeyword(inputVal)
  }

  const refreshList = () => doFetch(pageNoRef.current, pageSizeRef.current, keywordRef.current)

  const handleOpenCreate = () => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: '', description: '' }])
    setGroupModal({ visible: true, type: 'create' })
  }

  const handleOpenEdit = (record: IGroupItem) => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: record.name, description: record.description || '' }])
    setGroupModal({ visible: true, type: 'edit', record })
  }

  const checkBeforeDeleteGroup = (record: IGroupItem): boolean => {
    if (record.staffCount > 0) {
      failToast(t('e_pc_delete_group_has_staff_error', '此分组包含员工，无法删除'))
      return false
    }
    return true
  }

  const handleModalClose = () => {
    setGroupModal(prev => ({ ...prev, visible: false }))
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: '', description: '' }])
  }

  const { run: handleModalSubmit, loading: submitting } = useRequest(
    async () => {
      const { type, record } = groupModal
      if (type === 'edit' && record) {
        // 编辑模式：校验 createRows[0]
        const row = createRows[0]
        if (!row.name.trim()) {
          setCreateRows(prev => prev.map((r, i) => i === 0 ? { ...r, nameError: t('e_pc_input_group_name', '请输入分组名称') } : r))
          return
        }
        const updateRes = await updateGroup({
          groupId: record.id,
          name: row.name.trim(),
          description: row.description?.trim() || '',
        })
        if (!updateRes.success) return
        successToast(t('e_pc_update_success', '修改成功'))
        handleModalClose()
        refreshList()
      } else {
        // 批量新增：校验
        let hasError = false
        const validated = createRows.map(row => {
          if (!row.name.trim()) {
            hasError = true
            return { ...row, nameError: t('e_pc_input_group_name', '请输入分组名称') }
          }
          return row
        })
        if (hasError) {
          setCreateRows(validated)
          return
        }
        // 校验同名：只有非首次出现的行才标记 error
        const duplicateValidated = validated.map((row, i) => {
          const firstOccurrenceIndex = validated.findIndex(r => r.name.trim() === row.name.trim())
          const isDuplicate = firstOccurrenceIndex !== i
          if (isDuplicate) {
            return { ...row, nameError: t('e_pc_group_dept_exist', '存在重复分组，请调整') }
          }
          return row
        })
        const hasDuplicate = duplicateValidated.some(row => row.nameError === t('e_pc_group_dept_exist', '存在重复分组，请调整'))
        if (hasDuplicate) {
          setCreateRows(duplicateValidated)
          return
        }
        const batchRes = await createGroup({
          groupCreateList: createRows.map(row => ({
            name: row.name.trim(),
            description: row.description?.trim() || '',
          })),
        })
        if (!batchRes.success) return
        // 接口统一返回 code=0，通过 conflictList 区分：空=创建成功，非空=存在重名冲突
        const conflictList: IGroupConflictItem[] = batchRes.data?.conflictList || []
        if (conflictList.length > 0) {
          const conflictNames = new Set(conflictList.map(item => item.name))
          setCreateRows(prev => prev.map(row => {
            const isConflict = conflictNames.has(row.name.trim())
            return isConflict
              ? { ...row, nameError: t('e_pc_group_exist', '该分组名称已存在，请更换后重试') }
              : row
          }))
          return
        }
        successToast(t('e_pc_create_success', '创建成功'))
        handleModalClose()
        refreshList()
      }
    },
    { manual: true }
  )

  const { run: handleConfirmDelete, loading: deleting } = useRequest(
    async (group: IGroupItem) => {
      const res = await deleteGroup({ groupId: group.id })
      if (res.success) {
        successToast(t('e_pc_del_success', '删除成功'))
        setDeletingGroup(null)
        refreshList()
      }
    },
    { manual: true }
  )

  const handlePageChange = (_pageNo: number, _pageSize: number) => {
    _pageNo !== pageNo && setPageNo(_pageNo)
    _pageSize !== pageSize && setPageSize(_pageSize)
    doFetch(_pageNo, _pageSize, keywordRef.current)
  }

  const isEdit = groupModal.type === 'edit'

  const columns = [
    {
      dataIndex: 'name',
      title: t('e_pc_group_common', '分组'),
      render: (text: string) => <TipText text={text} />,
    },
    {
      dataIndex: 'description',
      title: t('e_pc_description', '描述'),
      render: (text: string) => <TipText text={text || '--'} />,
    },
    {
      dataIndex: 'staffCount',
      title: t('e_pc_staff_count', '员工数量'),
      width: 160,
    },
    {
      dataIndex: '',
      title: t('batch_common_operation', '操作'),
      width: 160,
      fixed: 'right',
      render: (_: never, record: IGroupItem) => (
        <div>
          <Button type="brand-text" onClick={() => handleOpenEdit(record)}>
            {t('e_pc_edit', '编辑')}
          </Button>&ensp;&ensp;&ensp;
          <PopConfirm
            content={t('e_pc_delete_group_confirm', '确认删除此分组？')}
            placement="left"
            onConfirm={() => handleConfirmDelete(record)}
            cancelText={t('e_pc_cancel', '取消')}
            confirmText={t('e_pc_submit', '确认')}
            disabled={deleting || record.staffCount > 0}
          >
            <Button
              type="brand-text"
              disabled={deleting}
              onClick={() => {
                checkBeforeDeleteGroup(record)
              }}
            >
              {t('e_pc_del', '删除')}
            </Button>
          </PopConfirm>
        </div>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <FormPro layout="vertical" size="small" labelCol={{ span: 12 }} wrapperCol={{ span: 12 }} style={{ paddingBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 12px', alignItems: 'end' }}>
          <FormPro.Item label={t('e_pc_group_common', '分组')} style={{ marginBottom: 0 }}>
            <Input
              allowClear
              placeholder={t('e_pc_input_group_name', '请输入分组名称')}
              value={inputVal}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputVal(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') handleSearch()
              }}
            />
          </FormPro.Item>
          <div style={{ gridColumn: 'span 3', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Button type="brand" size="normal" htmlType="button" onClick={handleSearch} className="btn-dark">
              {t('e_pc_aquire', '查询')}
            </Button>
          </div>
        </div>
      </FormPro>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="brand" size="normal" htmlType="button" onClick={handleOpenCreate}>
          {t('e_pc_add_group', '新增分组')}
        </Button>
      </div>

      <CommonTable
        rowKey="id"
        columns={columns}
        data={listData?.result || []}
        loading={loading}
        pagination={{
          total: listData?.totalCount || 0,
          currentPage: pageNo,
          pageSize,
          onChange: handlePageChange,
        }}
      />

      {/* 新增/编辑分组弹窗 */}
      <Modal
        title={isEdit ? t('e_pc_edit_group', '编辑分组') : t('e_pc_add_group', '新增分组')}
        visible={groupModal.visible}
        onCancel={handleModalClose}
        showCancelButton={false}
        showConfirmButton={false}
        closeMotion={false}
        backdropClosable={false}
        style={{ width: '680px' }}
      >
        <FormPro
          form={form}
          colon={false}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 18 }}
          onFinish={handleModalSubmit}
        >
          <div className={styles.modalFormList}>
            {createRows.map((row, index) => (
              <div key={row.key} className={`${styles.formListItem} ${isEdit ? styles.formListItemNoIndex : ''}`}>
                {!isEdit && <span className={styles.formListIndex}>{index + 1}</span>}
                <div className={styles.formListFields}>
                  <div className={styles.formListFieldsRow}>
                    <div className={styles.formListCol}>
                      <div className={styles.formListColLabel}>
                        <span className={styles.formListColRequired}>*&nbsp;</span>
                        {t('e_pc_group_common', '分组')}
                      </div>
                      <Input
                        value={row.name}
                        status={row.nameError ? 'error' : undefined}
                        placeholder={t('e_pc_input_group_name', '请输入分组名称')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowNameChange(index, e.target.value)}
                        onBlur={handleRowNameBlur}
                        style={{ width: '100%' }}
                      />
                      {row.nameError && <div className={styles.formListColError}>{row.nameError}</div>}
                    </div>
                    <div className={styles.formListCol}>
                      <div className={styles.formListColLabel}>{t('e_pc_description', '描述')}</div>
                      <Input
                        value={row.description}
                        placeholder={t('e_pc_input_group_desc', '请输入分组描述')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowDescChange(index, e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
                {!isEdit && createRows.length > 1 && (
                  <Button type="brand-text" onClick={() => handleRowRemove(index)} className={styles.formListDeleteBtn}>
                    <Icon name="trash-o" size={20} />
                  </Button>
                )}
              </div>
            ))}
          </div>
          {!isEdit && (
            <div className={styles.addRowBtnWrap}>
              <Button className={styles.addRowBtn} type="brand-text" onClick={handleRowAdd}>
              + {t('e_pc_add_another', '继续添加一条')}
              </Button>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px', gap: '12px' }}>
            <Button type="hollow" onClick={handleModalClose}>
              {t('e_pc_cancel_button', '取消')}
            </Button>
            <Button type="brand" onClick={() => handleModalSubmit()} loading={submitting}>
              {t('e_pc_confirm_button', '确认')}
            </Button>
          </div>
        </FormPro>
      </Modal>

    </div>
  )
}
